import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, MessageCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createEnquiry, getProducts } from "@/lib/api";
import { FARM_TYPES, SPECIES } from "@/lib/brand";
import { enquiryWhatsapp } from "@/lib/whatsapp";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EnquiryForm({ defaultProduct = "", source = "CONTACT_FORM", showQuantity = false, onDone }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    customer_name: "", mobile: "", email: "", company_name: "", location: "",
    farm_type: "", species: "", product_name: defaultProduct, quantity: "", message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  useEffect(() => {
    if (defaultProduct) setForm((f) => ({ ...f, product_name: defaultProduct }));
  }, [defaultProduct]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.customer_name.trim()) e.customer_name = "Please enter your name";
    const digits = form.mobile.replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 15) e.mobile = "Enter a valid mobile number";
    if (form.email && !EMAIL_RX.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Please enter a message";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await createEnquiry({ ...form, source });
      setSuccess(res);
      toast.success("Enquiry received successfully");
      onDone?.(res);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="enquiry-success" className="text-center py-4">
        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(63,174,53,0.12)" }}>
          <CheckCircle2 className="w-9 h-9" style={{ color: "#3FAE35" }} />
        </div>
        <h3 className="font-heading text-2xl font-bold" style={{ color: "#063B63" }}>Thank You for Contacting BIOSYN</h3>
        <p className="mt-2 text-sm" style={{ color: "#4A6B82" }}>
          Your enquiry has been received successfully. Our team will review it and contact you shortly.
        </p>
        <div className="mt-4 inline-block px-4 py-2 rounded-xl font-mono text-sm font-semibold" style={{ background: "#F5FAFC", color: "#087FBF" }}>
          Enquiry ID: {success.enquiry_number}
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/products" data-testid="success-back-products" className="btn-outline-blue">Back to Products</Link>
          <a
            href={enquiryWhatsapp({ productName: form.product_name, name: form.customer_name, location: form.location })}
            target="_blank" rel="noopener noreferrer"
            data-testid="success-whatsapp"
            className="btn-green"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp BIOSYN
          </a>
        </div>
      </div>
    );
  }

  const err = (k) => errors[k] && <p className="text-xs mt-1 text-red-500">{errors[k]}</p>;

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ef-name">Full Name *</Label>
          <Input id="ef-name" data-testid="ef-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Your name" className="mt-1.5" />
          {err("customer_name")}
        </div>
        <div>
          <Label htmlFor="ef-mobile">Mobile Number *</Label>
          <Input id="ef-mobile" data-testid="ef-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="Your mobile" className="mt-1.5" />
          {err("mobile")}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ef-email">Email Address</Label>
          <Input id="ef-email" data-testid="ef-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="mt-1.5" />
          {err("email")}
        </div>
        <div>
          <Label htmlFor="ef-company">Company / Farm Name</Label>
          <Input id="ef-company" data-testid="ef-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Optional" className="mt-1.5" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ef-location">Location / City</Label>
          <Input id="ef-location" data-testid="ef-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City / District" className="mt-1.5" />
        </div>
        <div>
          <Label>Farm Type</Label>
          <Select value={form.farm_type} onValueChange={(v) => set("farm_type", v)}>
            <SelectTrigger data-testid="ef-farm-type" className="mt-1.5"><SelectValue placeholder="Select farm type" /></SelectTrigger>
            <SelectContent>
              {FARM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Species</Label>
          <Select value={form.species} onValueChange={(v) => set("species", v)}>
            <SelectTrigger data-testid="ef-species" className="mt-1.5"><SelectValue placeholder="Select species" /></SelectTrigger>
            <SelectContent>
              {SPECIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Product Interested In</Label>
          <Select value={form.product_name} onValueChange={(v) => set("product_name", v)}>
            <SelectTrigger data-testid="ef-product" className="mt-1.5"><SelectValue placeholder="Select product" /></SelectTrigger>
            <SelectContent>
              {products.map((p) => <SelectItem key={p.slug} value={p.name}>{p.name}</SelectItem>)}
              <SelectItem value="Other / General Enquiry">Other / General Enquiry</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {showQuantity && (
        <div>
          <Label htmlFor="ef-qty">Quantity Required</Label>
          <Input id="ef-qty" data-testid="ef-quantity" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="e.g. 10 units" className="mt-1.5" />
        </div>
      )}

      <div>
        <Label htmlFor="ef-message">Message *</Label>
        <Textarea id="ef-message" data-testid="ef-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your requirement..." rows={4} className="mt-1.5" />
        {err("message")}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <button type="submit" data-testid="ef-submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-70">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send Enquiry <ArrowRight className="w-4 h-4" /></>}
        </button>
        <a
          href={enquiryWhatsapp({ productName: form.product_name, name: form.customer_name, location: form.location })}
          target="_blank" rel="noopener noreferrer"
          data-testid="ef-whatsapp"
          className="btn-green"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp Now
        </a>
      </div>
    </form>
  );
}
